import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_CONFIG } from '../config/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Tag, User, Clock, BookOpen,
  Eye, ThumbsUp, ChevronRight, Target, Star
} from 'lucide-react';
import SEOHead from '../components/common/SEOHead';
import { ResponsiveLogo } from '../components/common/Logo';
import { useAuth } from '../contexts/AuthContext';
import UnifiedNavigation from '../components/common/UnifiedNavigation';
import BreadcrumbNavigation from '../components/common/BreadcrumbNavigation';
import { supabase } from '../lib/supabase';
import { EduArticle, EduDomain, EduSubject, EduKnowledgePoint } from '../types/learning';
import toast from 'react-hot-toast';

const LearningArticlePreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState<EduArticle | null>(null);
  const [domain, setDomain] = useState<EduDomain | null>(null);
  const [subject, setSubject] = useState<EduSubject | null>(null);
  const [knowledgePoint, setKnowledgePoint] = useState<EduKnowledgePoint | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readingProgress, setReadingProgress] = useState(0);

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);


  // 提取HTML结果内容的函数
  const extractHtmlContent = useCallback((content: string): string => {
    if (!content) return '';
    
    const startMarker = '===HTML_RESULT_START===';
    const endMarker = '===HTML_RESULT_END===';
    
    const startIndex = content.indexOf(startMarker);
    const endIndex = content.indexOf(endMarker);
    
    if (startIndex === -1) {
      // 如果没有找到开始标记，返回原内容
      return content;
    }
    
    // 提取HTML内容（跳过开始标记）
    let extracted;
    if (endIndex === -1) {
      // 如果没有结束标记，提取从开始标记到文章末尾的内容
      extracted = content.substring(startIndex + startMarker.length).trim();
    } else {
      // 如果有结束标记，提取两个标记之间的内容
      extracted = content.substring(startIndex + startMarker.length, endIndex).trim();
    }
    
    return extracted;
  }, []);


  // 获取文章数据
  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) {
        setError('缺少文章ID');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // 使用公开API获取文章详情，未登录用户也可以访问
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/public-learning-content?action=article&id=${id}`, {
          headers: {
            'Content-Type': 'application/json',
            // 使用anon key，如果用户已登录则附加access_token用于个性化功能
            'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          const articleData = result.data;
          setArticle(articleData);
          
          // 设置层级信息（从API返回的数据中获取）
          setDomain(articleData.domain);
          setSubject(articleData.subject);
          setKnowledgePoint(articleData.knowledge_point);
        } else {
          throw new Error(result.error || '获取文章详情失败');
        }
      } catch (err) {
        console.error('获取文章详情失败:', err);
        setError(err instanceof Error ? err.message : '获取文章失败');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  // 删除原有的fetchDomain, fetchSubject, fetchKnowledgePoint函数，因为API已经返回了这些数据
  // 删除incrementReadCount函数，公开API会自动处理阅读计数

  // 阅读进度跟踪
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const element = contentRef.current;
      const windowHeight = window.innerHeight;
      const documentHeight = element.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = Math.min(100, Math.max(0, ((scrollTop - element.offsetTop + windowHeight) / documentHeight) * 100));
      
      setReadingProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // 早期返回必须在所有 hooks 之后
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">文章不存在</h2>
          <p className="text-gray-600 mb-4">抱歉，找不到您要查看的学习文章。</p>
          <button
            onClick={() => navigate('/learning/articles')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回文章列表
          </button>
        </div>
      </div>
    );
  }

  const htmlContent = extractHtmlContent(article?.content || '');
  const estimatedReadingTime = Math.ceil((htmlContent.length || 0) / 300);

  const seoData = {
    headline: article.title,
    description: article.summary || article.title,
    author: { name: article.author || '思维训练平台', url: window.location.origin },
    publisher: { name: '思维训练平台', url: window.location.origin, logo: `${window.location.origin}/logo.png` },
    datePublished: article.created_at,
    url: `${window.location.origin}/learning/article/${article.id}`,
  };

  const creativeWork = {
    name: article.title,
    description: article.summary || article.title,
    author: { name: article.author || '思维训练平台', url: window.location.origin },
    publisher: { name: '思维训练平台', url: window.location.origin, logo: `${window.location.origin}/logo.png` },
    dateCreated: article.created_at,
    url: `${window.location.origin}/learning/article/${article.id}`,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={article.title}
        description={article.summary || article.title}
        newsArticle={seoData}
        creativeWork={creativeWork}
      />
      
      {/* 顶部导航栏 */}
      <UnifiedNavigation currentPage="learning" />

      {/* 阅读进度条 */}
      <div className="fixed top-16 left-0 right-0 h-1 bg-gray-200 z-40">
        <div 
          className="h-full bg-blue-600 transition-all duration-150 ease-out"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* 页面内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 面包屑导航 */}
        <BreadcrumbNavigation 
          items={[
            { name: '思维训练', href: '/learning/articles' },
            ...(subject ? [{ name: subject.name }] : []),
            { name: article.title, current: true }
          ]}
          className="mb-6"
        />

        {/* 标题与摘要 */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 leading-tight">
            {article.title}
          </h1>
          {article.summary && (
            <p className="text-xl text-gray-600 leading-relaxed mb-4">
              {article.summary}
            </p>
          )}

          {/* 元信息 */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {new Date(article.created_at).toLocaleDateString()}
            </div>
            {article.author && (
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {article.author}
              </div>
            )}
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              约{estimatedReadingTime}分钟阅读
            </div>
            <div className="flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              {article.read_count} 次阅读
            </div>
            <div className="flex items-center">
              <ThumbsUp className="w-4 h-4 mr-1" />
              {article.like_count} 人喜欢
            </div>
            <div className="flex items-center">
              <BookOpen className="w-4 h-4 mr-1" />
              学习文章
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 主要内容区域 */}
          <div className="lg:col-span-3" ref={contentRef}>
            <div className="bg-white rounded-lg shadow-sm p-8">
              {article.cover_image && (
                <div className="mb-8">
                  <img
                    src={article.cover_image}
                    alt={article.title}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* 文章内容 */}
              <div className="prose prose-lg max-w-none">
                {htmlContent ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                    className="html-content"
                  />
                ) : (
                  <p className="text-gray-500 italic">暂无内容</p>
                )}
              </div>
            </div>
          </div>

          {/* 侧边栏 */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* 学习路径 */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  学习路径
                </h3>
                <div className="space-y-3">
                  {domain && (
                    <div className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-600">领域:</span>
                      <span className="ml-2 font-medium">{domain.name}</span>
                    </div>
                  )}
                  {subject && (
                    <div className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                      <span className="text-gray-600">学科:</span>
                      <span className="ml-2 font-medium">{subject.name}</span>
                    </div>
                  )}
                  {knowledgePoint && (
                    <div className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                      <span className="text-gray-600">知识点:</span>
                      <span className="ml-2 font-medium">{knowledgePoint.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 学习统计 */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Star className="w-5 h-5 mr-2 text-yellow-600" />
                  学习统计
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">阅读次数</span>
                    <span className="font-medium">{article.read_count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">点赞数</span>
                    <span className="font-medium">{article.like_count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">预计时长</span>
                    <span className="font-medium">{estimatedReadingTime}分钟</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">阅读进度</span>
                    <span className="font-medium">{Math.round(readingProgress)}%</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningArticlePreview;